import React, { useEffect, useState } from "react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import "./Engagement.css";
const blank = () => ({
  title: "",
  description: "",
  audience: "all",
  branch_id: "",
  anonymous: false,
  starts_at: "",
  ends_at: "",
  questions: [
    { question_text: "", question_type: "text", options: [], required: true },
  ],
});
export default function Engagement() {
  const { hasPermission, user } = useAuth(),
    manage = hasPermission("engagement.manage"),
    reportAccess = hasPermission("engagement.report"),
    canRespond = hasPermission("engagement.respond") && Boolean(user?.member_number);
  const [tab, setTab] = useState("surveys"),
    [rows, setRows] = useState([]),
    [selected, setSelected] = useState(null),
    [answers, setAnswers] = useState({}),
    [form, setForm] = useState(blank()),
    [feedback, setFeedback] = useState({
      category: "suggestion",
      subject: "",
      message: "",
    }),
    [feedbackRows, setFeedbackRows] = useState([]),
    [report, setReport] = useState(null),
    [message, setMessage] = useState("");
  const load = async () => {
    try {
      const r = await api.getEngagementSurveys();
      setRows(r.data || []);
      const f = await api.getMyFeedback();
      setFeedbackRows(f.data || []);
      if (reportAccess) {
        const x = await api.getEngagementReport();
        setReport(x.data);
      }
    } catch (e) {
      setMessage(e.message);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const open = async (id) => {
    try {
      const r = await api.getEngagementSurvey(id);
      setSelected(r.data);
      setAnswers({});
    } catch (e) {
      setMessage(e.message);
    }
  };
  const addQuestion = () =>
    setForm({
      ...form,
      questions: [
        ...form.questions,
        {
          question_text: "",
          question_type: "text",
          options: [],
          required: true,
        },
      ],
    });
  const qchange = (i, k, v) =>
    setForm({
      ...form,
      questions: form.questions.map((q, n) => (n === i ? { ...q, [k]: v } : q)),
    });
  const create = async (e) => {
    e.preventDefault();
    const invalidChoice = form.questions.find(
      (q) =>
        ["single_choice", "multiple_choice"].includes(q.question_type) &&
        (q.options || []).filter(Boolean).length < 2,
    );
    if (invalidChoice) {
      setMessage(
        "Every choice question needs at least two options separated by commas, for example: Online, Physical.",
      );
      return;
    }
    try {
      await api.createEngagementSurvey(form);
      setForm(blank());
      setMessage("Survey draft created.");
      await load();
    } catch (x) {
      setMessage(x.message);
    }
  };
  const transition = async (r, a) => {
    try {
      await api.transitionEngagementSurvey(r.id, a);
      await load();
    } catch (x) {
      setMessage(x.message);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.submitEngagementResponse(selected.id, answers);
      setMessage(r.message);
      await open(selected.id);
      await load();
    } catch (x) {
      setMessage(x.message);
    }
  };
  const sendFeedback = async (e) => {
    e.preventDefault();
    try {
      const r = await api.submitFeedback(feedback);
      setMessage(r.message);
      setFeedback({ category: "suggestion", subject: "", message: "" });
      await load();
    } catch (x) {
      setMessage(x.message);
    }
  };
  const handleFeedback = async (item, status) => {
    const staff_response = window.prompt(
      status === "under_review"
        ? "Optional internal/member note:"
        : "Write the official response to the member:",
      item.staff_response || "",
    );
    if (staff_response === null) return;
    try {
      const response = await api.handleFeedback(item.id, { status, staff_response });
      setMessage(response.message);
      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };
  const optionlessChoices = (selected?.questions || []).filter(
    (q) => q.question_type === "single_choice" && !q.options?.length,
  );
  const chooseLegacyOption = (questionId) => {
    const next = { ...answers };
    optionlessChoices.forEach((q) => {
      next[q.id] = q.id === questionId ? "Yes" : "No";
    });
    setAnswers(next);
  };
  return (
    <div className="engagement-page">
      <header>
        <span>MEMBER VOICE</span>
        <h1>Engagement Centre</h1>
        <p>Surveys, feedback and measurable member participation.</p>
      </header>
      <nav>
        {[
          "surveys",
          "feedback",
          ...(reportAccess ? ["reports"] : []),
        ].map((x) => (
          <button
            key={x}
            className={tab === x ? "active" : ""}
            onClick={() => setTab(x)}
          >
            {x}
          </button>
        ))}
      </nav>
      {message && <div className="engagement-message">{message}</div>}
      {tab === "surveys" && (
        <>
          {manage && (
            <form className="survey-builder" onSubmit={create}>
              <h2>Create survey</h2>
              <input
                required
                placeholder="Survey title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <textarea
                placeholder="Description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
              <div className="survey-row">
                <select
                  value={form.audience}
                  onChange={(e) =>
                    setForm({ ...form, audience: e.target.value })
                  }
                >
                  <option value="all">All members</option>
                  <option value="branch">Specific branch</option>
                </select>
                <label>
                  <input
                    type="checkbox"
                    checked={form.anonymous}
                    onChange={(e) =>
                      setForm({ ...form, anonymous: e.target.checked })
                    }
                  />{" "}
                  Anonymous answers
                </label>
                <input
                  type="datetime-local"
                  value={form.ends_at}
                  onChange={(e) =>
                    setForm({ ...form, ends_at: e.target.value })
                  }
                />
              </div>
              {form.questions.map((q, i) => (
                <div className="question-builder" key={i}>
                  <b>{i + 1}</b>
                  <input
                    required
                    placeholder="Question"
                    value={q.question_text}
                    onChange={(e) =>
                      qchange(i, "question_text", e.target.value)
                    }
                  />
                  <select
                    value={q.question_type}
                    onChange={(e) =>
                      qchange(i, "question_type", e.target.value)
                    }
                  >
                    <option value="text">Text</option>
                    <option value="rating">Rating 1-5</option>
                    <option value="single_choice">Single choice</option>
                    <option value="multiple_choice">Multiple choice</option>
                  </select>
                  {q.question_type.includes("choice") && (
                          <input
                            required
                            placeholder="Options separated by comma"
                      onChange={(e) =>
                        qchange(
                          i,
                          "options",
                          e.target.value.split(",").map((x) => x.trim()),
                        )
                      }
                    />
                  )}
                </div>
              ))}
              <div>
                <button type="button" onClick={addQuestion}>
                  + Question
                </button>
                <button>Create draft</button>
              </div>
            </form>
          )}
          <section className="survey-grid">
            {rows.map((r) => (
              <article key={r.id}>
                <span>{r.status}</span>
                <h3>{r.title}</h3>
                <p>{r.description || "Member survey"}</p>
                <small>
                  {r.question_count} questions · {r.response_count} responses
                </small>
                <div>
                  <button onClick={() => open(r.id)}>Open</button>
                  {manage && r.status === "draft" && (
                    <button onClick={() => transition(r, "publish")}>
                      Publish
                    </button>
                  )}
                  {manage && r.status === "published" && (
                    <button onClick={() => transition(r, "close")}>
                      Close
                    </button>
                  )}
                </div>
              </article>
            ))}
          </section>
          {selected && (
            <div className="survey-modal">
              <section>
                <button className="close" onClick={() => setSelected(null)}>
                  ×
                </button>
                <h2>{selected.title}</h2>
                {selected.has_responded ? (
                  <p className="done">
                    You have already submitted this survey.
                  </p>
                ) : (
                  <form onSubmit={submit}>
                    {selected.questions.map((q) => (
                      <label key={q.id}>
                        {q.question_text}
                        {q.question_type === "text" && (
                          <textarea
                            required={q.required}
                            onChange={(e) =>
                              setAnswers({ ...answers, [q.id]: e.target.value })
                            }
                          />
                        )}{" "}
                        {q.question_type === "rating" && (
                          <select
                            required
                            onChange={(e) =>
                              setAnswers({ ...answers, [q.id]: e.target.value })
                            }
                          >
                            <option value="">Rate</option>
                            {[1, 2, 3, 4, 5].map((x) => (
                              <option key={x} value={x}>{x}</option>
                            ))}
                          </select>
                        )}
                        {q.question_type === "single_choice" &&
                          q.options.length > 0 && q.options.map((o) => (
                            <span key={o}>
                              <input
                                type="radio"
                                name={`q${q.id}`}
                                onChange={() =>
                                  setAnswers({ ...answers, [q.id]: o })
                                }
                              />
                              {o}
                            </span>
                          ))}
                        {q.question_type === "single_choice" &&
                          q.options.length === 0 && (
                            <span className="legacy-choice">
                              <input
                                type="radio"
                                name={`survey-choice-${selected.id}`}
                                checked={answers[q.id] === "Yes"}
                                onChange={() => chooseLegacyOption(q.id)}
                              />
                              Select {q.question_text}
                            </span>
                          )}
                      </label>
                    ))}
                    {canRespond && <button>Submit response</button>}
                    {!canRespond && manage && (
                      <small>This is an administrative preview. A linked active member account is required to submit.</small>
                    )}
                  </form>
                )}
              </section>
            </div>
          )}
        </>
      )}
      {tab === "feedback" && (
        <div className={`feedback-layout ${manage ? "staff-inbox" : ""}`}>
          {!manage && <form onSubmit={sendFeedback}>
            <h2>Send feedback</h2>
            <select
              value={feedback.category}
              onChange={(e) =>
                setFeedback({ ...feedback, category: e.target.value })
              }
            >
              {[
                "service",
                "benefits",
                "workplace",
                "suggestion",
                "complaint",
                "other",
              ].map((x) => (
                <option key={x} value={x}>{x}</option>
              ))}
            </select>
            <input
              required
              placeholder="Subject"
              value={feedback.subject}
              onChange={(e) =>
                setFeedback({ ...feedback, subject: e.target.value })
              }
            />
            <textarea
              required
              placeholder="Your feedback"
              value={feedback.message}
              onChange={(e) =>
                setFeedback({ ...feedback, message: e.target.value })
              }
            />
            <button>Submit feedback</button>
          </form>}
          <section>
            {feedbackRows.map((f) => (
              <article key={f.id}>
                <b>{f.subject}</b>
                <span>{f.status}</span>
                {manage && <small className="feedback-member">{f.member_name} · {f.member_number} · {f.category}</small>}
                <p>{f.message}</p>
                {f.staff_response && (
                  <small>Response: {f.staff_response}</small>
                )}
                {manage && <div className="feedback-actions"><button onClick={()=>handleFeedback(f,"under_review")}>Review</button><button onClick={()=>handleFeedback(f,"resolved")}>Respond & resolve</button><button onClick={()=>handleFeedback(f,"closed")}>Close</button></div>}
              </article>
            ))}
            {feedbackRows.length===0&&<div className="engagement-message">No feedback has been submitted yet.</div>}
          </section>
        </div>
      )}
      {tab === "reports" && report && (
        <div className="engagement-reports">
          <div className="report-heading"><div><h2>Engagement report</h2><p>Response totals and answer analysis for every survey.</p></div><div className="feedback-summary">{(report.feedback_by_status||[]).map(item=><span key={item.status}><b>{item.total}</b>{item.status.replaceAll("_"," ")}</span>)}</div></div>
          {report.surveys.map((r) => (
            <article className="survey-report-card" key={r.id}>
              <header><div><span>{r.status}{Number(r.anonymous)===1?" · Anonymous":""}</span><h3>{r.title}</h3></div><strong>{r.responses}<small>{Number(r.responses)===1?"response":"responses"}</small></strong></header>
              <section>{(r.questions||[]).map((q,index)=><div className="question-report" key={q.id}><div className="question-report-title"><b>{index+1}. {q.question_text}</b>{q.average!==null&&q.average!==undefined&&<span>Average {q.average}/5</span>}</div>{q.question_type==="text"?<div className="text-answer-list">{(q.text_answers||[]).map((answer,i)=><p key={`${q.id}-${i}`}>{answer}</p>)}{!q.text_answers?.length&&<small>No written answers.</small>}</div>:<div className="answer-bars">{(q.breakdown||[]).map(item=><div key={item.label}><div><b>{item.label}</b><span>{item.count} · {item.percentage}%</span></div><i><em style={{width:`${item.percentage}%`}}/></i></div>)}{!q.breakdown?.length&&<small>No answers.</small>}</div>}</div>)}</section>
            </article>
          ))}
          {!report.surveys.length&&<div className="engagement-message">No survey report is available yet.</div>}
        </div>
      )}
    </div>
  );
}
